/**
 * CLUB-198: normalize/validate what the Invite-member modal collects (an email
 * OR a phone number with a country) into a single invite the API can act on.
 * Kept as a pure function so both the modal and the mutation can rely on the
 * same rules and it is trivially unit-tested.
 */

/**
 * Dial codes for the countries offered in the invite modal's picker. Kept in
 * step with `INVITE_COUNTRIES` in @wehunt/ui's InviteByEmailOrPhoneModal (same
 * set + codes) and with the mobile phone picker's `allowedCountries` — the
 * markets the apps sell in. A country the picker offers but this map omits
 * would normalize against Sweden's +46, so the two must list the same codes.
 */
const DIAL_CODES: Record<string, string> = {
  SE: '+46', FI: '+358', NO: '+47', AT: '+43', BE: '+32', DK: '+45',
  EE: '+372', FR: '+33', DE: '+49', HU: '+36', IT: '+39', LU: '+352',
  NL: '+31', PL: '+48', SK: '+421', ES: '+34', CH: '+41', UA: '+380', GB: '+44',
}

export type MemberInvite =
  | { channel: 'email'; value: string }
  | { channel: 'sms'; value: string }

export type ParseInviteResult =
  | { ok: true; invite: MemberInvite }
  | { ok: false; error: string }

// Deliberately simple — the backend is the source of truth for deliverability;
// this only catches obviously-malformed input before a round trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function parseInviteInput(raw: { email?: string; phone?: string; country?: string }): ParseInviteResult {
  const email = (raw.email ?? '').trim()
  const phone = (raw.phone ?? '').trim()

  if (email && phone) return { ok: false, error: 'Enter an email or a phone number, not both.' }
  if (!email && !phone) return { ok: false, error: 'Enter an email or a phone number.' }

  if (email) {
    if (!EMAIL_RE.test(email)) return { ok: false, error: 'Enter a valid email address.' }
    return { ok: true, invite: { channel: 'email', value: email.toLowerCase() } }
  }

  const normalized = normalizePhone(phone, raw.country ?? 'SE')
  if (!normalized) return { ok: false, error: 'Enter a valid phone number.' }
  return { ok: true, invite: { channel: 'sms', value: normalized } }
}

export type ParseInviteBatchResult =
  | { ok: true; invites: MemberInvite[] }
  | { ok: false; error: string }

/**
 * Normalize what the invite modal collects into a list of invites. A
 * comma-separated email paste becomes one invite per address; a phone, or a
 * single email, is one invite. One bad address refuses the whole batch (and
 * returns its specific reason) so nothing is half-accepted. Shared by both
 * invite surfaces — the published-hunt action and the plan wizard — so the
 * split rule has one home and cannot drift between them.
 */
export function parseInviteBatch(raw: { email?: string; phone?: string; country?: string }): ParseInviteBatchResult {
  const email = raw.email ?? ''
  const emails = email.includes(',')
    ? email.split(',').map((e) => e.trim()).filter(Boolean)
    : null
  // Fall back to the raw input when the split yields nothing (an email of only
  // commas/whitespace): parseInviteInput then rejects it as invalid rather than
  // leaving an empty batch that reports success having added nothing.
  const inputs =
    emails && emails.length > 0
      ? emails.map((e) => ({ email: e, phone: '', country: raw.country }))
      : [raw]

  const parsed = inputs.map((input) => parseInviteInput(input))
  const bad = parsed.find((p) => !p.ok)
  if (bad && !bad.ok) return { ok: false, error: bad.error }
  return { ok: true, invites: parsed.flatMap((p) => (p.ok ? [p.invite] : [])) }
}

/** Best-effort E.164 normalization using the selected country's dial code. */
function normalizePhone(input: string, country: string): string | null {
  const dial = DIAL_CODES[country] ?? DIAL_CODES.SE
  const cleaned = input.replace(/[\s\-().]/g, '')

  let e164: string
  if (cleaned.startsWith('+')) {
    e164 = cleaned
  } else if (cleaned.startsWith('00')) {
    e164 = `+${cleaned.slice(2)}`
  } else if (cleaned.startsWith('0')) {
    // National number with a trunk 0 → replace the 0 with the country dial code.
    e164 = `${dial}${cleaned.slice(1)}`
  } else {
    e164 = `${dial}${cleaned}`
  }

  return /^\+\d{8,15}$/.test(e164) ? e164 : null
}
